import { useCallback, useEffect, useState } from 'react';
import type { PublishedBrief } from 'fym-shared/src/releases/types';
import { useTRPCAPI, useTRPCQuery } from '../../../trpc/trpc';
import { hadSessionAtBoot } from './sessionAtBoot';
import { BriefAnswer, isFreshInstall, mayHaveUnseenNotes, resolveCache, shouldOpenAfterUpdate } from './whatsNewRules';
import { markWhatsNewSeen, readWhatsNewState, WhatsNewState, writeWhatsNewCache } from './whatsNewStore';

/** One answer per version is enough: an admin publishes a brief once. */
const BRIEF_STALE_MS = 60 * 60 * 1000;

interface Input {
  userId: number | null;
  /** The version of this bundle (`appInformation.version`). */
  running: string;
  locale: string;
  /** The page address names this version (`?whatsNew=`): ask for its notes even when seen. */
  askAnyway: boolean;
}

export interface WhatsNewAfterUpdate {
  /** The notes of the running version: a successful answer, else the cache, else null. */
  brief: PublishedBrief | null;
  /** True when the call for the notes ended (with an answer or an error). */
  settled: boolean;
  /** The panel should open after an update (the rules of spec 2026-10-20 What's new §7). */
  shouldOpen: boolean;
  /** "Got it": stores the running version as seen. */
  markSeen: () => void;
}

const readState = (userId: number | null): WhatsNewState | null => (userId === null ? null : readWhatsNewState(userId));

/**
 * The "after an update" half of the web panel. A fresh browser stores the running version and
 * shows nothing. Otherwise, when the running version is newer than "last seen", it asks
 * `releases.get` and opens only with one entry or more. The last answer is cached for a page with
 * no connection; a successful null forgets it.
 */
export function useWhatsNew({ userId, running, locale, askAnyway }: Input): WhatsNewAfterUpdate {
  const trpcAPI = useTRPCAPI();
  const [entry, setEntry] = useState(() => ({ userId, state: readState(userId) }));
  // A sign-in or a sign-out changes the user: read the value of the new user in this render.
  const current = entry.userId === userId ? entry : { userId, state: readState(userId) };
  if (current !== entry) {
    setEntry(current);
  }
  const lastSeen = current.state?.lastSeen ?? null;
  const fresh =
    userId !== null && isFreshInstall({ hasStoredLastSeen: lastSeen !== null, hadSessionAtBoot: hadSessionAtBoot() });

  useEffect(() => {
    // A fresh browser starts current: it never shows notes of a release it was not here for.
    if (userId !== null && fresh && running !== '') {
      setEntry({ userId, state: markWhatsNewSeen(userId, running) });
    }
  }, [userId, fresh, running]);

  const enabled = userId !== null && (askAnyway || (!fresh && mayHaveUnseenNotes(running, lastSeen)));
  const query = useTRPCQuery(
    trpcAPI.releases.get.queryOptions(
      { version: running, locale, surface: 'web' },
      { enabled, retry: false, staleTime: BRIEF_STALE_MS }
    )
  );

  const answer: BriefAnswer =
    query.isSuccess && query.data !== undefined
      ? { status: 'success', brief: query.data }
      : { status: query.isError ? 'error' : 'pending' };

  const { isSuccess, data } = query;
  useEffect(() => {
    if (userId === null || !isSuccess || data === undefined) {
      return;
    }
    writeWhatsNewCache(userId, running, data);
  }, [userId, running, isSuccess, data]);

  const cached = current.state?.cache?.version === running ? current.state.cache.brief : null;
  const brief = enabled ? resolveCache(answer, cached) : null;
  const shouldOpen =
    userId !== null &&
    shouldOpenAfterUpdate({ running, lastSeen, freshInstall: fresh, hasEntries: (brief?.entries.length ?? 0) > 0 });

  const markSeen = useCallback(() => {
    if (userId !== null && running !== '') {
      setEntry({ userId, state: markWhatsNewSeen(userId, running) });
    }
  }, [userId, running]);

  return { brief, settled: answer.status !== 'pending' || cached !== null, shouldOpen, markSeen };
}
